import type { Knowledge, KnowledgeGroup } from '../../types';
import { GROUP_COLORS } from '../notes/groupColors';
import { formatDate } from '../notes/noteUtils';
import LinkifiedText from '../LinkifiedText';

interface KnowledgeCardProps {
  item: Knowledge;
  group?: KnowledgeGroup;
  onView: (item: Knowledge) => void;
  onEdit: (item: Knowledge) => void;
  onDelete: (item: Knowledge) => void;
}

export default function KnowledgeCard({
  item,
  group,
  onView,
  onEdit,
  onDelete,
}: KnowledgeCardProps) {
  const groupStyle = group
    ? GROUP_COLORS[group.color] ?? GROUP_COLORS.primary
    : null;

  return (
    <div
      className="group flex cursor-pointer flex-col rounded-xl border-2 border-orange-500 bg-background-50 p-4 transition hover:border-orange-600 sm:rounded-lg"
      onClick={() => onView(item)}
    >
      <h3 className="mb-2 line-clamp-2 font-heading text-base font-semibold text-foreground-900">
        {item.title}
      </h3>

      <p className="mb-3 line-clamp-4 flex-1 text-sm leading-relaxed text-foreground-500">
        {item.content ? (
          <LinkifiedText text={item.content} />
        ) : (
          'Không có nội dung'
        )}
      </p>

      <div className="mb-3 flex flex-wrap items-center gap-1.5">
        {group && groupStyle && (
          <span
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${groupStyle.badge}`}
          >
            <span className={`h-1.5 w-1.5 rounded-full ${groupStyle.dot}`} />
            {group.name}
          </span>
        )}
        {item.attachments?.length > 0 && (
          <span className="inline-flex items-center gap-1 rounded-full bg-background-100 px-2.5 py-1 text-xs font-medium text-foreground-500">
            <i className="ri-attachment-2 text-xs" />
            {item.attachments.length}
          </span>
        )}
      </div>

      <div className="flex items-center justify-between border-t-2 border-orange-500/60 pt-3">
        <span className="text-xs text-foreground-400">
          Cập nhật {formatDate(item.updated_at)}
        </span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(item);
            }}
            className="flex h-10 w-10 items-center justify-center rounded-lg text-foreground-400 transition hover:bg-background-100 hover:text-foreground-700 sm:h-8 sm:w-8"
            aria-label="Chỉnh sửa"
          >
            <i className="ri-edit-line text-lg" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(item);
            }}
            className="flex h-10 w-10 items-center justify-center rounded-lg text-foreground-400 transition hover:bg-accent-100 hover:text-accent-700 sm:h-8 sm:w-8"
            aria-label="Xóa"
          >
            <i className="ri-delete-bin-line text-lg" />
          </button>
        </div>
      </div>
    </div>
  );
}